import { ListItem, ListItemButton, Paper, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { BoltDisplayField } from "@WahooFitness/crux-js-display-cfg";
import {
  WfIconContentSubtractFill,
  WfIconContentDraggableRegular,
  WfIconNavigationChevronRightRegular,
  WfButton,
} from "@WahooFitness/redesignr";
import noop from "@/services/noop";
import { MouseEvent, useMemo } from "react";
import useWorkoutPageFieldStrings from "../useWorkoutPageFieldStrings";

function WorkoutPageField({
  field,
  isEditable,
  onFieldClick = noop,
  onFieldRemove = noop,
  fieldPosition,
  isFirstField,
  isLastField,
  id,
}: {
  field?: BoltDisplayField;
  isEditable: boolean;
  onFieldClick?: () => void;
  onFieldRemove?: (e: MouseEvent<Element>) => void;
  fieldPosition?: number;
  isFirstField?: boolean;
  isLastField?: boolean;
  id: string;
}) {
  const { workoutFieldTranslations } = useWorkoutPageFieldStrings();
  const fieldName = useMemo(() => {
    if (field) {
      return workoutFieldTranslations[field.getId()];
    }
  }, [field, workoutFieldTranslations]);

  return (
    <ListItem
      key={id}
      component={Paper}
      elevation={4}
      disablePadding
      divider
      sx={{
        overflow: "clip",
        "&.MuiListItem-root.MuiPaper-rounded": {
          borderRadius: 0,
          ...(isFirstField && {
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }),
          ...(isLastField && {
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            borderBottom: "none",
          }),
        },
      }}
    >
      <ListItemButton sx={{ height: 48, pl: 1, pr: 2 }} disableGutters onClick={onFieldClick}>
        <Box display="flex" paddingRight={1.5} alignItems="center">
          {isEditable && (
            <WfButton
              size="large"
              iconOnly
              hierarchy="text"
              intent="destructive"
              onClick={onFieldRemove}
              aria-label="Remove field"
            >
              <WfIconContentSubtractFill fontSize="medium" color="error" />
            </WfButton>
          )}
          <Typography color="text.disabled" align="right" width="1.5rem" noWrap>
            {fieldPosition}
          </Typography>
        </Box>
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="space-between"
          flexGrow={1}
          minWidth={0}
        >
          <Typography noWrap>{fieldName}</Typography>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            {isEditable && <WfIconContentDraggableRegular sx={{ color: "text.disabled" }} />}
            {isEditable && <WfIconNavigationChevronRightRegular />}
          </Box>
        </Stack>
      </ListItemButton>
    </ListItem>
  );
}

export default WorkoutPageField;
